import * as THREE from 'three';
import Time from '../Utils/Time';
import Application from '../Application';
export class CameraKeyframeInstance {
    constructor(keyframe) {
        this.position = keyframe.position;
        this.focalPoint = keyframe.focalPoint;
    }
    update() { }
}
const keys = {
    idle: {
        position: new THREE.Vector3(-20000, 12000, 20000),
        focalPoint: new THREE.Vector3(0, -1000, 0),
    },
    monitor: {
        position: new THREE.Vector3(0, 950, 1900 - 7000),
        focalPoint: new THREE.Vector3(0, 950, 0 - 7000),
    },
    desk: {
        position: new THREE.Vector3(0, 1800, 5500),
        focalPoint: new THREE.Vector3(0, 500, 0),
    },
    loading: {
        position: new THREE.Vector3(-35000, 35000, 35000),
        focalPoint: new THREE.Vector3(0, -5000, 0),
    },
    orbitControlsStart: {
        position: new THREE.Vector3(-15000, 10000, 15000),
        focalPoint: new THREE.Vector3(-100, 350, 0),
    },
    freeLook: {
        position: new THREE.Vector3(0, 1000, 0),
        focalPoint: new THREE.Vector3(0, 1000, -100),
    },
    debug: {
        position: new THREE.Vector3(0, 2000, 5000),
        focalPoint: new THREE.Vector3(0, 2000, 0),
    },
};
export class MonitorKeyframe extends CameraKeyframeInstance {
    constructor() {
        const keyframe = keys.monitor;
        super(keyframe);
        this.application = new Application();
        this.sizes = this.application.sizes;
        this.origin = new THREE.Vector3().copy(keyframe.position);
        this.targetPos = new THREE.Vector3().copy(keyframe.position);
    }
    update() {
        const aspect = this.sizes.height / this.sizes.width;
        const additionalZoom = this.sizes.width < 768 ? 0 : 600;
        this.targetPos.z = this.origin.z + aspect * 1200 - additionalZoom;
        this.position.copy(this.targetPos);
    }
}
export class LoadingKeyframe extends CameraKeyframeInstance {
    constructor() {
        const keyframe = keys.loading;
        super(keyframe);
    }
    update() { }
}
export class DeskKeyframe extends CameraKeyframeInstance {
    constructor() {
        const keyframe = keys.desk;
        super(keyframe);
        this.application = new Application();
        this.mouse = this.application.mouse;
        this.sizes = this.application.sizes;
        this.origin = new THREE.Vector3().copy(keyframe.position);
        this.targetFoc = new THREE.Vector3().copy(keyframe.focalPoint);
        this.targetPos = new THREE.Vector3().copy(keyframe.position);
    }
    update() {
        this.targetFoc.x +=
            (this.mouse.x - this.sizes.width / 2 - this.targetFoc.x) * 0.05;
        this.targetFoc.y +=
            (-(this.mouse.y - this.sizes.height) - this.targetFoc.y) * 0.05;
        this.targetPos.x +=
            (this.mouse.x - this.sizes.width / 2 - this.targetPos.x) * 0.025;
        this.targetPos.y +=
            (-(this.mouse.y - this.sizes.height * 2) - this.targetPos.y) *
                0.025;
        const aspect = this.sizes.height / this.sizes.width;
        this.targetPos.z = this.origin.z + aspect * 3000 - 1800;
        this.focalPoint.copy(this.targetFoc);
        this.position.copy(this.targetPos);
    }
}
export class IdleKeyframe extends CameraKeyframeInstance {
    constructor() {
        const keyframe = keys.idle;
        super(keyframe);
        this.origin = new THREE.Vector3().copy(keyframe.position);
        this.time = new Time();
    }
    update() {
        this.position.x =
            Math.sin((this.time.elapsed + 19000) * 0.00008) * this.origin.x;
        this.position.y =
            Math.sin((this.time.elapsed + 1000) * 0.000004) * 4000 +
                this.origin.y -
                3000;
        this.position.z = this.position.z;
    }
}
export class OrbitControlsStart extends CameraKeyframeInstance {
    constructor() {
        const keyframe = keys.orbitControlsStart;
        super(keyframe);
    }
    update() { }
}
export class FreeLookKeyframe extends CameraKeyframeInstance {
    constructor() {
        const keyframe = keys.freeLook;
        super(keyframe);
        this.application = new Application();
        this.mouse = this.application.mouse;
        this.yaw = -Math.PI / 2; // Initial look direction
    }
    update() {
        if (this.application.camera.freeCam && this.mouse.isDown) {
            const sensitivity = 0.001;
            const deltaX = this.mouse.x - this.mouse.lastX;
            this.yaw -= deltaX * sensitivity; // Invert drag direction for natural feel
            // Update last position for next frame
            this.mouse.lastX = this.mouse.x;
            this.mouse.lastY = this.mouse.y;
        }
        // Calculate focal point from yaw
        // Fixed distance of 1000 units
        this.focalPoint.x = this.position.x + Math.cos(this.yaw) * 1000;
        this.focalPoint.z = this.position.z + Math.sin(this.yaw) * 1000;
        this.focalPoint.y = this.position.y; // Look straight ahead (pitch locked)
    }
}
export class DebugKeyframe extends CameraKeyframeInstance {
    constructor() {
        const keyframe = keys.debug;
        super(keyframe);
        this.application = new Application();
        this.keysPressed = {};
        this.yaw = -Math.PI / 2;
        this.pitch = 0;
        this.speed = 40; // Movement speed
        this.sensitivity = 0.002;
        window.addEventListener('keydown', (e) => (this.keysPressed[e.code] = true));
        window.addEventListener('keyup', (e) => (this.keysPressed[e.code] = false));
        document.addEventListener('mousemove', (e) => {
            if (document.pointerLockElement === this.application.renderer.instance.domElement) {
                this.yaw -= e.movementX * this.sensitivity;
                this.pitch -= e.movementY * this.sensitivity;
                // Clamp pitch to avoid flipping
                this.pitch = Math.max(-Math.PI / 2 + 0.1, Math.min(Math.PI / 2 - 0.1, this.pitch));
            }
        });
    }
    update() {
        // Only update if we are the active keyframe (handled by Camera.ts calling update)
        // But we might want to check if pointer lock is active to allow movement
        if (document.pointerLockElement !== this.application.renderer.instance.domElement)
            return;
        const rotation = new THREE.Euler(this.pitch, this.yaw, 0, 'YXZ');
        const forward = new THREE.Vector3(0, 0, -1).applyEuler(rotation);
        const right = new THREE.Vector3(1, 0, 0).applyEuler(rotation);
        if (this.keysPressed['ArrowUp'] || this.keysPressed['KeyW']) {
            this.position.add(forward.clone().multiplyScalar(this.speed));
            // Re-normalize for next use if needed, but we created new vectors
        }
        if (this.keysPressed['ArrowDown'] || this.keysPressed['KeyS']) {
            this.position.add(forward.clone().multiplyScalar(-this.speed));
        }
        if (this.keysPressed['ArrowRight'] || this.keysPressed['KeyD']) {
            this.position.add(right.clone().multiplyScalar(this.speed));
        }
        if (this.keysPressed['ArrowLeft'] || this.keysPressed['KeyA']) {
            this.position.add(right.clone().multiplyScalar(-this.speed));
        }
        // Optional: Fly up/down with E/Q or Space/Shift
        if (this.keysPressed['KeyE'] || this.keysPressed['Space']) {
            this.position.y += this.speed;
        }
        if (this.keysPressed['KeyQ'] || this.keysPressed['ShiftLeft']) {
            this.position.y -= this.speed;
        }
        // Calculate focal point
        const lookDir = new THREE.Vector3(Math.sin(this.yaw) * Math.cos(this.pitch), Math.sin(this.pitch), Math.cos(this.yaw) * Math.cos(this.pitch));
        // Correcting the math:
        // x = cos(yaw) * cos(pitch)
        // y = sin(pitch)
        // z = sin(yaw) * cos(pitch)
        // Wait, standard spherical coords:
        // x = r * sin(theta) * cos(phi)
        // y = r * cos(theta)
        // z = r * sin(theta) * sin(phi)
        // Using ThreeJS camera convention (Y up, -Z forward):
        // vector = (0,0,-1)
        // Rotate X (pitch), Rotate Y (yaw)
        this.focalPoint.copy(this.position).add(forward.clone().multiplyScalar(100)); // Look 100 units away
    }
}
